import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Search, X, Eye } from 'lucide-react';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';

const ACTION_COLORS = {
  INSERT: { bg: 'rgba(16,185,129,0.15)', color: '#10b981' },
  UPDATE: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
  DELETE: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
};

function fmtDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function diffFields(oldV, newV) {
  if (!oldV && !newV) return [];
  if (typeof oldV !== 'object' || typeof newV !== 'object' || !oldV || !newV) {
    return [{ field: '(payload)', old: oldV, new: newV }];
  }
  const keys = new Set([...Object.keys(oldV), ...Object.keys(newV)]);
  const out = [];
  for (const k of keys) {
    const a = oldV[k];
    const b = newV[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      out.push({ field: k, old: a, new: b });
    }
  }
  return out;
}

function fmtValue(v) {
  if (v === null || v === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (typeof v === 'object') return <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>;
  return String(v);
}

export default function AuditLog() {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    action: '', search: '', dateFrom: '', dateTo: '',
  });
  const [refetchTick, setRefetchTick] = useState(0);

  const [filterOptions, setFilterOptions] = useState({ tables: [], users: [], actions: [] });

  const [detail, setDetail] = useState(null);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get('/api/audit-log/filters');
      setFilterOptions(res.data || { tables: [], users: [], actions: [] });
    } catch { /* ignore */ }
  }, []);

  const fetchData = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum, pageSize };
      Object.keys(filters).forEach(k => { if (filters[k]) params[k] = filters[k]; });
      const res = await axios.get('/api/audit-log', { params });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || 'Error loading audit log.' });
    }
    setLoading(false);
  }, [filters, page, t]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchData(1); /* eslint-disable-next-line */ }, [refetchTick]);

  const applyFilters = () => setRefetchTick(x => x + 1);
  const clearFilters = () => {
    setFilters({ action: '', search: '', dateFrom: '', dateTo: '' });
    setRefetchTick(x => x + 1);
  };

  const openDetail = (row) => setDetail(row);
  const closeDetail = () => setDetail(null);

  const detailDiff = detail ? diffFields(detail.old_values, detail.new_values) : [];

  return (
    <div>
      <div className="page-header">
        <h1>{t('audit.title') || 'Audit Log'}</h1>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 240 }}>
            <label className="form-label" style={{ fontSize: 12 }}>{t('common.search') || 'Search'}</label>
            <input className="form-input" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
              placeholder={t('audit.searchPh') || 'Search by employee, user, values or reason...'} />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <label className="form-label" style={{ fontSize: 12 }}>{t('audit.action') || 'Action'}</label>
            <select className="form-input" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
              <option value="">{t('audit.allActions') || 'All actions'}</option>
              {filterOptions.actions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div style={{ flex: '0 0 160px' }}>
            <label className="form-label" style={{ fontSize: 12 }}>{t('common.from') || 'From'}</label>
            <DateInput className="form-input" value={filters.dateFrom} onChange={v => setFilters(f => ({ ...f, dateFrom: v }))} />
          </div>
          <div style={{ flex: '0 0 160px' }}>
            <label className="form-label" style={{ fontSize: 12 }}>{t('common.to') || 'To'}</label>
            <DateInput className="form-input" value={filters.dateTo} onChange={v => setFilters(f => ({ ...f, dateTo: v }))} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} onClick={applyFilters} disabled={loading}>
            <Search size={14} style={{ marginRight: 6 }} />{t('common.apply') || 'Apply'}
          </button>
          <button className="btn-secondary" style={{ width: 'auto', padding: '8px 20px' }} onClick={clearFilters}>
            <X size={14} style={{ marginRight: 6 }} />{t('common.clear') || 'Clear'}
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>
            {total} {t('audit.entries') || 'entries'}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{t('audit.date') || 'Date'}</th>
                <th>{t('audit.user') || 'User'}</th>
                <th>{t('audit.action') || 'Action'}</th>
                <th>{t('audit.reason') || 'Reason'}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20 }}>{t('common.loading') || 'Loading…'}</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>{t('audit.empty') || 'No audit entries.'}</td></tr>
              )}
              {!loading && rows.map(r => {
                const ac = ACTION_COLORS[r.action] || { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
                return (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtDateTime(r.created_at)}</td>
                    <td>{r.username || '-'}</td>
                    <td>
                      <span className="badge" style={{ background: ac.bg, color: ac.color, fontWeight: 700 }}>{r.action}</span>
                    </td>
                    <td style={{ fontSize: 13, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason || ''}>
                      {r.reason || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>
                      <button className="btn-icon" title={t('audit.viewDetail') || 'View detail'} onClick={() => openDetail(r)}>
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > pageSize && (
          <div style={{ marginTop: 12 }}>
            <Pagination page={page} limit={pageSize} total={total} onPageChange={p => fetchData(p)} />
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Modal isOpen={!!detail} onClose={closeDetail} title={t('audit.detailTitle') || 'Change detail'} xl>
        {detail && (
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('audit.date') || 'Date'}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 13 }}>{fmtDateTime(detail.created_at)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('audit.user') || 'User'}</div>
                <div>{detail.username || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('audit.action') || 'Action'}</div>
                <div>
                  <span className="badge" style={{
                    background: (ACTION_COLORS[detail.action] || {}).bg || 'var(--bg-tertiary)',
                    color: (ACTION_COLORS[detail.action] || {}).color || 'var(--text-secondary)',
                    fontWeight: 700,
                  }}>{detail.action}</span>
                </div>
              </div>
            </div>

            {detail.reason && (
              <div style={{ padding: 10, marginBottom: 16, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius)', borderLeft: '3px solid var(--accent)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('audit.reason') || 'Reason'}</div>
                <div style={{ fontSize: 13 }}>{detail.reason}</div>
              </div>
            )}

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {detailDiff.length === 0 ? (t('audit.noChanges') || 'No changes detected.') : `${detailDiff.length} ${detailDiff.length === 1 ? 'campo' : 'campos'}`}
              </div>
              {detailDiff.length > 0 && (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('audit.field') || 'Field'}</th>
                        <th>{t('audit.oldValue') || 'Old value'}</th>
                        <th>{t('audit.newValue') || 'New value'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailDiff.map((d, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{d.field}</td>
                          <td style={{ background: 'rgba(239,68,68,0.05)', fontSize: 13 }}>{fmtValue(d.old)}</td>
                          <td style={{ background: 'rgba(16,185,129,0.05)', fontSize: 13 }}>{fmtValue(d.new)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
